/**
 * La prueba de vida activa: comprobar que hay una persona, ahora, delante de la cámara.
 *
 * ── EMPIEZA POR AQUÍ: LO QUE ESTE MÓDULO **NO** PUEDE HACER SOLO ────────────────
 *
 * El servidor sortea la secuencia, la guarda y compara contra lo que él pidió. Todo eso está
 * bien y hace falta. **Y no alcanza.**
 *
 * Porque `afirmadoPorElCliente` lo dice el cliente. Si el motor de visión corre en el
 * navegador, quien afirma "sí, parpadeó" es el navegador — y un atacante que lo modifica
 * afirma los gestos, en el orden que le pidieron, con milisegundos plausibles.
 *
 * O sea: el sorteo protege **el orden**. No protege **la detección**. Un reto impredecible
 * contestado por un mentiroso sigue siendo un reto contestado.
 *
 * **La única defensa real es que el servidor vea los píxeles.** Desde #79 los fotogramas
 * suben y el servidor mide rPPG y PRNU — esa es la vida que pesa. Por eso la secuencia pudo
 * bajar de 4 a 2 gestos sin perder nada que importe (ver GESTOS_POR_SECUENCIA).
 */

/**
 * Los gestos que se pueden pedir.
 *
 * Movimientos que un vídeo pregrabado no puede improvisar y que una foto no puede hacer.
 */
export type Gesto =
  /** Mirar de frente. Es el ancla: sin esto no hay con qué comparar el resto. */
  | 'frente'
  | 'izquierda'
  | 'derecha'
  | 'sonreir'
  | 'parpadear';

/** Los que se sortean. `frente` no entra: siempre va primero, es el ancla. */
export const GESTOS_SORTEABLES: readonly Gesto[] = ['izquierda', 'derecha', 'sonreir', 'parpadear'];

/**
 * Cuántos gestos tiene una secuencia. DOS: `frente` (el ancla — y el momento en que la
 * pantalla captura el selfie) más UNO sorteado.
 *
 * Eran cuatro, como los frames del Figma. Bajó por decisión de producto tras las pruebas
 * reales: cuatro gestos cansan a la persona honesta y no compran seguridad proporcional —
 * contra el vídeo grabado basta UN gesto impredecible, y contra el cliente modificado no
 * protege ninguna cantidad de coreografía: protege el servidor mirando los píxeles
 * (rPPG + PRNU, que ya corren). Más gestos solo suben el abandono.
 */
export const GESTOS_POR_SECUENCIA = 2;

export interface IntentoDeGesto {
  gesto: Gesto;
  /** Milisegundos desde que se pidió el gesto hasta que el cliente dijo haberlo detectado. */
  ms: number;

  /**
   * **Lo AFIRMA el cliente. No es un hecho nuestro.**
   *
   * El nombre es largo a propósito. Se llamaba `detectado`, y ese nombre invita a creérselo:
   * suena a que alguien detectó algo. Nadie detectó nada — un navegador que no controlamos
   * dijo que sí.
   */
  afirmadoPorElCliente: boolean;
}

/**
 * Lo mínimo que tarda un cuello humano.
 *
 * 250 ms para girar la cabeza y detectarlo no lo hace una persona: lo hace un vídeo saltando
 * al fotograma correcto. Que sea *demasiado bueno* es la señal.
 *
 * **Y NO hay máximo, a propósito.** Una persona mayor tarda. Una nerviosa tarda. Poner un
 * tope castigaría exactamente a quien más ayuda necesita, y no atraparía a nadie.
 */
export const MS_MINIMOS_POR_GESTO = 250;

/**
 * Sortea la secuencia. **En el servidor.**
 *
 * `frente` siempre va primero: es el ancla contra la que se compara todo lo demás. El resto
 * sale barajado sin repetir — repetir daría pistas y desperdicia un reto.
 *
 * `aleatorio` se inyecta para poder probar esto. En producción es `Math.random`; en los tests,
 * una función determinista. Un generador que no se puede fijar no se puede probar.
 */
export function sortearSecuencia(aleatorio: () => number = Math.random): Gesto[] {
  const resto = [...GESTOS_SORTEABLES];

  // Fisher-Yates. No es adorno académico: barajar con `sort(() => aleatorio() - 0.5)` produce
  // permutaciones sesgadas —hay órdenes que salen mucho más que otros— y un reto sesgado es
  // un reto más fácil de adivinar.
  for (let i = resto.length - 1; i > 0; i--) {
    const j = Math.floor(aleatorio() * (i + 1));
    [resto[i], resto[j]] = [resto[j]!, resto[i]!];
  }

  return ['frente', ...resto.slice(0, GESTOS_POR_SECUENCIA - 1)];
}

export type EstadoPrueba =
  /** El servidor vio los píxeles y hay una persona viva. */
  | 'superada'
  /**
   * La coreografía cuadró, pero la detección la afirmó el cliente.
   *
   * **No es aprobar.** Es el mismo estado que `lista_no_disponible` en sanciones y que
   * `costo: null` en el catálogo: no lo sabemos, y decirlo es lo único honesto.
   */
  | 'sin_verificar'
  | 'fallada'
  | 'sospechosa';

export interface ResultadoPruebaDeVida {
  estado: EstadoPrueba;
  /** Cuántos gestos se completaron. */
  completados: number;
  /**
   * Para el expediente y para el motor de riesgo. **Nunca para la pantalla.**
   *
   * Decirle a quien falló QUÉ gesto falló le enseña exactamente qué ajustar en el siguiente
   * intento. Al honesto tampoco le sirve.
   */
  detalle: string;
}

/**
 * ¿Cuadró la coreografía?
 *
 * ── LOS GESTOS VAN EN ORDEN, Y NO ES CAPRICHO ──────────────────────────────────
 *
 * Aceptarlos desordenados convertiría la secuencia en un CONJUNTO, y un conjunto se resuelve
 * con clips sueltos reproducidos en cualquier orden.
 *
 * ── Y SIN DETECCIÓN DEL SERVIDOR, LO MÁXIMO ES `sin_verificar` ─────────────────
 *
 * `deteccionDelServidor` es un parámetro y no una constante para que encenderlo sea explícito
 * y se vea en la revisión — no un `if` que alguien cambia sin que nadie lo note.
 */
export function evaluarPruebaDeVida(
  secuencia: readonly Gesto[],
  intentos: readonly IntentoDeGesto[],
  deteccionDelServidor = false,
): ResultadoPruebaDeVida {
  if (secuencia.length === 0) {
    // Sin reto no hay prueba. Devolver "superada" aquí sería aprobar a todo el mundo.
    return { estado: 'fallada', completados: 0, detalle: 'No se pidió ninguna prueba de vida.' };
  }

  let completados = 0;

  for (const [i, gesto] of secuencia.entries()) {
    const intento = intentos[i];

    if (intento === undefined) {
      return {
        estado: 'fallada',
        completados,
        detalle: `Se pidieron ${secuencia.length} gestos y llegaron ${intentos.length}.`,
      };
    }

    if (intento.gesto !== gesto) {
      return {
        estado: 'fallada',
        completados,
        detalle:
          `En la posición ${i + 1} se pidió "${gesto}" y llegó "${intento.gesto}". El orden es ` +
          `parte del reto: si no importara, unos clips sueltos bastarían.`,
      };
    }

    if (!intento.afirmadoPorElCliente) {
      return { estado: 'fallada', completados, detalle: `No se detectó "${gesto}".` };
    }

    if (intento.ms < MS_MINIMOS_POR_GESTO) {
      // Demasiado rápido NO es "fallada": es SOSPECHOSA. La diferencia importa — fallar es
      // algo que le pasa a la gente honesta todo el rato; ser imposiblemente rápido, no.
      return {
        estado: 'sospechosa',
        completados,
        detalle:
          `"${gesto}" se completó en ${intento.ms} ms. Un cuello humano no hace eso: es un ` +
          `vídeo saltando al fotograma, o imágenes inyectadas en la cámara.`,
      };
    }

    completados++;
  }

  if (!deteccionDelServidor) {
    return {
      estado: 'sin_verificar',
      completados,
      detalle:
        `Los ${completados} gestos llegaron en orden y a velocidad humana, pero la detección la ` +
        `AFIRMÓ EL CLIENTE: el servidor no vio los píxeles. Un navegador modificado afirma lo ` +
        `mismo sin que haya nadie delante de la cámara. Esto no es una verificación.`,
    };
  }

  return {
    estado: 'superada',
    completados,
    detalle: `${completados} de ${secuencia.length} gestos, verificados por el servidor.`,
  };
}

/**
 * Lo que se le dice a la PERSONA cuando no pasa.
 *
 * ── QUÉ HACER, NUNCA POR QUÉ FALLÓ ─────────────────────────────────────────────
 *
 * Cada detalle que se muestre es información gratis para quien lo está intentando engañar.
 * `sin_verificar` se le cuenta como un éxito, y es correcto: **la persona hizo su parte**.
 */
export function mensajeDePruebaDeVida(r: ResultadoPruebaDeVida): string {
  if (r.estado === 'superada' || r.estado === 'sin_verificar') return 'Listo.';
  return 'No pudimos completar la prueba. Busca un sitio con buena luz y vuelve a intentarlo.';
}
